/**
 * PostgradSiblingsWidget — compact "Natural next step" strip on
 * undergrad DegreeDetail pages. Renders up to 4 postgrad degrees at
 * the same university, prioritising same-subject-area matches. Silent
 * when the base degree is already PG or there are no matches.
 *
 * Added 2026-02-05 to close the loop between UG and PG discovery
 * (Backlog P3 · "Related postgrad routes at this uni").
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const REASON_LABEL = {
  same_subject_area: "Same subject",
  same_subject_slug: "Related subject",
  same_uni: "Same university",
};

export default function PostgradSiblingsWidget({ degreeId, level }) {
  const [siblings, setSiblings] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!degreeId) return;
    // Skip the fetch entirely for PG pages — the backend also short-
    // circuits but avoiding the round-trip keeps DegreeDetail snappy.
    if (level && level.toLowerCase() !== "undergraduate") {
      setLoaded(true);
      return;
    }
    api
      .get(`/degrees/${degreeId}/postgrad-siblings?limit=4`)
      .then((r) => setSiblings(r.data?.siblings || []))
      .catch(() => setSiblings([]))
      .finally(() => setLoaded(true));
  }, [degreeId, level]);

  if (!loaded || siblings.length === 0) return null;

  return (
    <div
      className="mt-4 rounded-md border border-stone-200 bg-stone-50 p-3"
      data-testid="degree-postgrad-siblings"
    >
      <div className="flex items-center gap-1.5 text-[10px] font-underground-sc tracking-[0.18em] text-stone-500 mb-2">
        <GraduationCap className="h-3 w-3" aria-hidden />
        Natural next step · postgrad routes at this uni
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {siblings.map((s) => {
          const label =
            s.title ||
            (s.subject_area && s.qualification
              ? `${s.qualification} — ${s.subject_area}`
              : s.subject_area || s.qualification || s.id);
          return (
            <Link
              key={s.id}
              to={`/degree/${s.id}`}
              data-testid={`postgrad-sibling-${s.id}`}
              className="group flex items-center justify-between gap-2 rounded border border-stone-200 bg-white px-3 py-2 hover:border-[#4C1D95] hover:bg-[#F5F0FF] transition"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-stone-800 truncate">
                  {label}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {s.qualification && s.title && (
                    <span className="text-[11px] text-stone-500">
                      {s.qualification}
                    </span>
                  )}
                  {REASON_LABEL[s.sibling_reason] && (
                    <span className="text-[9px] font-underground-sc tracking-wider text-[#4C1D95] bg-[#F5F0FF] rounded-full px-1.5 py-0.5">
                      {REASON_LABEL[s.sibling_reason]}
                    </span>
                  )}
                </div>
              </div>
              <ArrowRight
                className="h-3.5 w-3.5 text-stone-400 group-hover:text-[#4C1D95] shrink-0"
                aria-hidden
              />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
